import React, { useEffect, useRef, useState } from 'react';
import { CompactError, Modal, RobbieMark, Skeleton } from '../../ui';
import { useReadOnly } from '../../../contexts/WizardAccessContext';
import useSmartBomVersions from './useSmartBomVersions';
import useSmartBomReveal from './useSmartBomReveal';
import SmartBomModal from './SmartBomModal';
import RfqCriteriaBrief, { useAskRobbieToChange } from '../../criteria/RfqCriteriaBrief';

/**
 * The pre-discovery checkpoint: continuing past validation is the moment
 * supplier research fires, run against the BOM exactly as it stands (target
 * prices included) and the buyer's sourcing criteria. So this gate
 * interposes on EVERY research start (not only when something new
 * happened): the buyer always gets the chance to vet or tweak the Smart BOM
 * first, because a change discovered after dispatch means researching again. When the BOM
 * has fresh unseen improvements the dialog celebrates them (Robbie's
 * reveal); otherwise it is a quiet confirm with the review one click away.
 * Only a missing BOM or a failed version lookup passes straight through —
 * the gate must never be able to block the journey outright. It opens at
 * once: while the versions load, the change count shimmers in place. A
 * research start that failed reopens it with the error inline.
 *
 * Robbie-voiced surface (AI-carrying content — the licensed violet), with
 * the editable Smart BOM sheet embedded.
 */

/** Presentational dialog — the gallery renders this with sample numbers. */
export interface SmartBomCheckpointDialogProps {
  open: boolean;
  latestVersion: number;
  changeCount: number;
  /** Fresh unseen improvement — celebratory copy, review as primary. */
  isFresh: boolean;
  /** True once the buyer has opened the sheet from this dialog. */
  hasReviewed: boolean;
  canEdit: boolean;
  onReview: () => void;
  onProceed: () => void;
  onCancel: () => void;
  /** The criteria research runs with — a compact `CriteriaBrief` under the body. */
  criteria?: React.ReactNode;
  /** The version list is still loading: the change count shimmers. */
  loading?: boolean;
  /** The last research start failed; shown inline, and Start becomes Try again. */
  error?: string | null;
}

const quietBtn =
  `inline-flex h-8 items-center rounded-control px-s4 text-body-sm font-medium
   text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink
   focus:outline-none focus-visible:ring-2 focus-visible:ring-action`;
const cobaltBtn =
  `inline-flex h-8 items-center rounded-control bg-action px-s5 text-body-sm
   font-semibold text-white transition-colors hover:bg-action-hover
   focus:outline-none focus-visible:ring-2 focus-visible:ring-action focus-visible:ring-offset-1`;
const robbieBtn =
  `inline-flex h-8 items-center rounded-control bg-robbie px-s5 text-body-sm
   font-semibold text-white transition-opacity hover:opacity-90
   focus:outline-none focus-visible:ring-2 focus-visible:ring-robbie focus-visible:ring-offset-1`;

export const SmartBomCheckpointDialog: React.FC<SmartBomCheckpointDialogProps> = ({
  open,
  latestVersion,
  changeCount,
  isFresh,
  hasReviewed,
  canEdit,
  onReview,
  onProceed,
  onCancel,
  criteria,
  loading = false,
  error = null,
}) => {
  const changesText = loading ? (
    <Skeleton width={72} height={12} className="inline-block align-middle" />
  ) : (
    `${changeCount} change${changeCount === 1 ? '' : 's'}`
  );
  const startLabel = error ? 'Try again' : 'Start supplier research';
  const vetText = canEdit ? 'look it over or tweak values' : 'look it over';
  return (
    <Modal open={open} onClose={onCancel} size="md" title="Before I find suppliers">
      <div className="flex gap-s4">
        <RobbieMark size="sm" state="insight" aria-label="Robbie has an insight" />
        <div className="min-w-0 flex-1">
          {isFresh || loading ? (
            <p className="m-0 text-body-md text-ink">
              Your BOM is now{' '}
              <span className="font-mono text-mono-data">v{latestVersion}</span> — {changesText}{' '}
              smarter than the file you uploaded.
            </p>
          ) : (
            <p className="m-0 text-body-md text-ink">
              Ready to research suppliers for your Smart BOM{' '}
              <span className="font-mono text-mono-data">v{latestVersion}</span>?
            </p>
          )}
          <p className="m-0 mt-s2 text-body-sm text-ink-body">
            Research runs against exactly this BOM and these criteria — quantities and target
            prices included — so this is the moment to {vetText}. Changing the BOM afterwards
            means researching again.
          </p>
          {criteria && <div className="mt-s3">{criteria}</div>}
          {error && (
            <CompactError layout="row" title="Couldn't start research" message={error} className="mt-s3" />
          )}
        </div>
      </div>
      <div className="mt-s6 flex flex-wrap items-center justify-end gap-s3">
        {hasReviewed ? (
          <>
            <button type="button" onClick={onReview} className={quietBtn}>
              Look again
            </button>
            <button type="button" onClick={onProceed} className={cobaltBtn}>
              {startLabel}
            </button>
          </>
        ) : isFresh && error ? (
          <>
            <button type="button" onClick={onReview} className={quietBtn}>
              Review the smarter BOM
            </button>
            <button type="button" onClick={onProceed} className={cobaltBtn}>
              Try again
            </button>
          </>
        ) : isFresh || loading ? (
          <>
            <button type="button" onClick={onProceed} className={quietBtn}>
              Start without looking
            </button>
            <button type="button" onClick={onReview} className={robbieBtn}>
              Review the smarter BOM
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onReview} className={quietBtn}>
              Review Smart BOM first
            </button>
            <button type="button" onClick={onProceed} className={cobaltBtn}>
              {startLabel}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export interface SmartBomCheckpointProps {
  bomId: string | null;
  /** The buyer pressed Continue on validation — the checkpoint decides. */
  open: boolean;
  /** Back to validation without starting research. */
  onCancel: () => void;
  /** Start supplier research (the original Continue). */
  onProceed: () => void;
  /** Why the last start failed, shown inline. */
  error?: string | null;
}

export const SmartBomCheckpoint: React.FC<SmartBomCheckpointProps> = ({
  bomId,
  open,
  onCancel,
  onProceed,
  error = null,
}) => {
  const readOnly = useReadOnly();
  const state = useSmartBomVersions(bomId);
  const reveal = useSmartBomReveal(bomId, state.versions);
  const [reviewOpen, setReviewOpen] = useState(false);
  const askToChange = useAskRobbieToChange(() => {
    setReviewOpen(false);
    onCancel();
  });
  const [hasReviewed, setHasReviewed] = useState(false);

  // The list was fetched when the step loaded — the buyer has typically
  // changed the BOM since (that is the whole point of validation). Re-fetch
  // at the moment the gate OPENS so the dialog and sheet show the BOM as it
  // stands now. Keyed on the open transition alone (tracked in a ref), so it
  // can never re-fire on re-renders regardless of the hook's function
  // identities — that dependency was a fetch loop once.
  const refreshRef = useRef(state.refresh);
  refreshRef.current = state.refresh;
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened && bomId) refreshRef.current();
  }, [open, bomId]);

  // Research runs on what is here now, so the gate interposes on EVERY start —
  // pass-through only when it cannot honestly show anything: no BOM id, or
  // the version list failed to load (the gate must never block the journey).
  // Fires once per opening.
  const passedRef = useRef(false);
  const listSettled = state.versions !== null || state.error !== null;
  const nothingToShow = state.versions === null && state.error !== null;
  useEffect(() => {
    if (!open) {
      passedRef.current = false;
      setHasReviewed(false);
      return;
    }
    if (passedRef.current) return;
    if (!error && (!bomId || nothingToShow)) {
      passedRef.current = true;
      onProceed();
    }
  }, [open, bomId, nothingToShow, onProceed, error]);

  if (!open || !bomId) return null;

  if (nothingToShow && !error) return null; // pass-through effect handles proceeding

  const latestVersion =
    reveal.latestVersion ||
    (state.versions && state.versions.length > 0
      ? state.versions[state.versions.length - 1].version
      : 1);

  return (
    <>
      {!reviewOpen && (
        <SmartBomCheckpointDialog
          open
          latestVersion={latestVersion}
          changeCount={reveal.changeCount}
          isFresh={reveal.show}
          hasReviewed={hasReviewed}
          canEdit={!readOnly}
          onReview={() => {
            reveal.acknowledge();
            setHasReviewed(true);
            setReviewOpen(true);
          }}
          onProceed={() => {
            reveal.acknowledge();
            onProceed();
          }}
          onCancel={onCancel}
          loading={!listSettled}
          error={error}
          criteria={<RfqCriteriaBrief variant="compact" labelled={false} onAsk={onCancel} />}
        />
      )}
      <SmartBomModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        initialTab="smart-bom"
        versions={state.versions}
        selectedVersion={state.selectedVersion}
        detail={state.detail}
        loading={state.loadingList || state.loadingDetail}
        error={state.error}
        canEdit={!readOnly}
        saving={state.saving}
        downloading={state.downloading}
        savedNotice={state.savedNotice}
        onSelectVersion={state.selectVersion}
        onSave={state.saveEdits}
        onDownload={state.download}
        onStartResearch={() => {
          setReviewOpen(false);
          onProceed();
        }}
        onAskRobbie={askToChange}
        criteria={<RfqCriteriaBrief surface="none" hideAsk />}
      />
    </>
  );
};

export default SmartBomCheckpoint;
